/**
 * useEquipmentLibrary — 2026-06-08 Phase 1E wire-up for the canonical
 * Q Manual VG35002 weapons catalog REST surface.
 *
 * Fetches the weapons manifest from the briefing-room backend +
 * exposes the 3-state union (idle/loading/ready/error) per MEMORY.md
 * rule `feedback_async_then_render_needs_loading_state`. Plus an
 * on-demand detail loader for the currently-selected weapon entry —
 * list endpoint returns compact summaries only; full JSON loads on click.
 *
 * Mirrors useModulesLibrary.ts pattern: injected WeaponsServiceModule for
 * test stubbing + useMemo-stabilized default instance + cancelled-flag
 * for unmount safety.
 *
 * Scope: this phase covers the WEAPONS category only (the route + seed +
 * service module are all weapons-specific in Phase 1C/1D). Vehicles +
 * Devices + Drugs + Q Branch NPCs come in subsequent phases.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WeaponManifestEntry } from '../services/host/WeaponsServiceModule';
import { WeaponsServiceModule } from '../services/host/WeaponsServiceModule';

export type EquipmentLibraryState = 'idle' | 'loading' | 'ready' | 'error';

export interface EquipmentLibraryHookResult {
  state: EquipmentLibraryState;
  weapons: WeaponManifestEntry[];
  error: string | null;
  refresh: () => void;
  /** Currently-selected weapon id (null = none). */
  selectedWeaponId: string | null;
  /** Full JSON of the selected weapon (null = not loaded). */
  selectedWeaponData: Record<string, unknown> | null;
  /** State of the per-weapon detail fetch (independent of list state). */
  selectedWeaponState: EquipmentLibraryState;
  selectWeapon: (weaponId: string | null) => void;
}

export function useEquipmentLibrary(
  weaponsService?: WeaponsServiceModule,
): EquipmentLibraryHookResult {
  const resolvedService = useMemo(
    () => weaponsService ?? new WeaponsServiceModule(),
    [weaponsService],
  );

  // Manifest state
  const [state, setState] = useState<EquipmentLibraryState>('idle');
  const [weapons, setWeapons] = useState<WeaponManifestEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Per-weapon detail state
  const [selectedWeaponId, setSelectedWeaponId] = useState<string | null>(null);
  const [selectedWeaponData, setSelectedWeaponData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedWeaponState, setSelectedWeaponState] =
    useState<EquipmentLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectWeapon = useCallback((weaponId: string | null) => {
    setSelectedWeaponId(weaponId);
    if (weaponId === null) {
      setSelectedWeaponData(null);
      setSelectedWeaponState('idle');
    }
  }, []);

  // Manifest fetch
  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setWeapons(result.weapons);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setWeapons([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  // Per-weapon detail fetch — fires when selectedWeaponId changes
  useEffect(() => {
    if (selectedWeaponId === null) {
      return;
    }
    let cancelled = false;

    setSelectedWeaponState('loading');
    setSelectedWeaponData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedWeaponId);
        if (cancelled) return;
        if (result === null) {
          setSelectedWeaponState('error');
          return;
        }
        setSelectedWeaponData(result);
        setSelectedWeaponState('ready');
      } catch {
        if (cancelled) return;
        setSelectedWeaponState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedWeaponId]);

  return {
    state,
    weapons,
    error,
    refresh,
    selectedWeaponId,
    selectedWeaponData,
    selectedWeaponState,
    selectWeapon,
  };
}
